import { forwardRef, memo, useRef } from 'react';
import type { BaseTextFieldProps, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { FormHelperText, InputAdornment, InputLabel, type TextFieldProps, Tooltip } from '@mui/material';
import { StyledTextField, Wrapper } from './TextField.styles';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';
import { getIcon } from '../../../utils/common.ts';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';
import type { InputBaseProps } from '@mui/material/InputBase';
import type { ViewItem } from '../../../types/catalogViewItem.ts';
import { useIsOverflowing } from '../../../utils/hooks';

export interface Props extends Omit<TextFieldProps, 'variant'> {
  label?: string;
  suffix?: string;
  className?: string;
  background?: 'transparent' | 'white';
  readOnly?: boolean;
  disableHelperText?: boolean;
  dataTestId?: string;
  helperTextDataTestId?: string;
  formItemUiDefinition?: FormItemUiDefinition | null;
  viewItem?: ViewItem;
  lightError?: boolean;
}

export const TextField = memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        className,
        label,
        readOnly,
        error,
        lightError,
        helperText,
        slotProps,
        suffix = '',
        disableHelperText = false,
        dataTestId,
        helperTextDataTestId,
        value,
        formItemUiDefinition,
        viewItem,
        inputRef: inputRefProp,
        ...props
      },
      ref
    ) => {
      const inputRef = useRef<HTMLInputElement | null>(null);
      const setInputRef = (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof inputRefProp === 'function') {
          inputRefProp(node);
        } else if (inputRefProp) {
          (inputRefProp as MuiTextFieldProps['inputRef'] & { current: HTMLInputElement | null }).current = node;
        }
      };
      const { isOverflowing } = useIsOverflowing(inputRef, value ?? '', 0);

      const styles = getUiDefinitionTextStyles(formItemUiDefinition ?? viewItem?.visibilityResult?.formItemUiDefinition);
      const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition ?? viewItem?.visibilityResult?.formItemUiDefinition);
      const Icon =
        (formItemUiDefinition?.icon ?? viewItem?.visibilityResult?.formItemUiDefinition)
          ? getIcon(formItemUiDefinition?.icon ?? viewItem?.visibilityResult?.formItemUiDefinition?.icon ?? '')
          : null;

      return (
        <Wrapper>
          {!!label && <InputLabel disabled={props.disabled}>{label}</InputLabel>}
          <Tooltip title={isOverflowing && value ? value.toString() : ''} disableHoverListener={!isOverflowing || !value}>
            <StyledTextField
              ref={ref}
              inputRef={setInputRef}
              value={value ?? ''}
              variant="outlined"
              className={className}
              error={error}
              lightError={lightError}
              slotProps={{
                ...slotProps,
                htmlInput: {
                  ...slotProps?.htmlInput,
                  'data-testid': dataTestId ?? null,
                },
                input: {
                  ...slotProps?.input,
                  sx: { ...(slotProps?.input as BaseTextFieldProps)?.sx, ...styles, ...boxStyles },
                  readOnly,
                  startAdornment: Icon ? (
                    <InputAdornment position="start">
                      <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
                    </InputAdornment>
                  ) : (
                    ((slotProps?.input as InputBaseProps)?.startAdornment ?? null)
                  ),
                  endAdornment: (slotProps?.input as InputBaseProps)?.endAdornment ? (
                    (slotProps?.input as InputBaseProps)?.endAdornment
                  ) : suffix.length ? (
                    <InputAdornment position="end">{suffix}</InputAdornment>
                  ) : null,
                },
              }}
              {...props}
            />
          </Tooltip>
          {!disableHelperText && helperText && (
            <FormHelperText data-testid={helperTextDataTestId} error={error} variant="filled">
              {helperText}
            </FormHelperText>
          )}
        </Wrapper>
      );
    }
  )
);
